<template>
  <div>
    <bpmn-diagram-actions-shape-top-transform
      ref="changeShape"
      :selectedEvent="selectedEvent"
      :base-data="baseData"
      :svgGraph="svgGraph"
      :svgGroups="svgGroups"
      :svgLanes="svgLanes"
    >
      <template #buttons>
        <span></span>
      </template>
    </bpmn-diagram-actions-shape-top-transform>
    <div class="action-menu">
      <div class="action-button-group">

        <!-- transform shape -->
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-menu density="compact" scrim="true">
              <template v-slot:activator="{ props: changeShapeProps }">
                <div
                  v-bind="{...props, ...(transformMenuDisabled ? {} : changeShapeProps)}"
                  class="action-button"
                  :class="{'disabled': transformMenuDisabled}"
                >
                  <v-icon size="small">mds:category</v-icon>
                </div>
              </template>

              <v-list>
                <v-list-item
                  prepend-icon="custom:TaskIcon"
                  :active="selectedEvent.type === 'bpmn:Task' && !selectedEvent.subprocess"
                  :disabled="transformMenuDisabled"
                  @click="!transformMenuDisabled && $refs.changeShape.transformShape('event')"
                >
                  <v-list-item-title>Event</v-list-item-title>
                </v-list-item>
                <v-list-item
                  prepend-icon="custom:DecisionIcon"
                  :active="selectedEvent.type === 'bpmn:ExclusiveGateway'"
                  :disabled="transformMenuDisabled"
                  @click="!transformMenuDisabled && $refs.changeShape.transformShape('decision')"
                >
                  <v-list-item-title>Decision</v-list-item-title>
                </v-list-item>
                <v-list-item
                  prepend-icon="custom:SubworkflowIcon"
                  :active="selectedEvent.type === 'bpmn:Task' && !!selectedEvent.subprocess"
                  :disabled="transformMenuDisabled"
                  @click="!transformMenuDisabled && $refs.changeShape.transformShape('subprocess')"
                >
                  <v-list-item-title>Subprocess</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template #default>
            <div>
              <p v-if="!transformMenuDisabled" class="text-body-2 mb-1">
                Change shape
              </p>
              <p v-else class="text-body-2 mb-1">
                Change shape (disabled because of {{ transformDisabledReason }})
              </p>
            </div>
          </template>
        </v-tooltip>

        <!-- move down in event group -->
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              tabindex="0"
              data-test="move-down-button"
              class="action-button captures-description"
              :class="{'disabled': !atTheBottomOfLane || disableSingleSelectionActions}"
              @click="atTheBottomOfLane && !disableSingleSelectionActions && moveDownInEventGroup()"
            >
              <v-icon size="small">mds:arrow-downward</v-icon>
            </div>
          </template>
          <div>
            <p v-if="disableSingleSelectionActions" class="text-body-2 mb-1">
              Expand lane disabled (multiple events selected)
            </p>
            <p v-else-if="atTheBottomOfLane" class="text-body-2 mb-1">
              Expand lane and move down this shape
            </p>
            <p v-else class="text-body-2 mb-1">
              Expand lane disabled (use drag & drop to move down)
            </p>
          </div>
        </v-tooltip>

        <bpmn-delete-connection-dialog
          :open="confirmDialog"
          :connections="connections"
          @close="confirmDialog = false"
          @delete-connection="deleteConnection"
        ></bpmn-delete-connection-dialog>

        <!-- change color -->
        <workflow-settings-card-type-color-picker
          appearance="diagram"
          variant="events"
          :color="color"
          @change="changeColor"
        >
          <template #activator="{ props: onMenu }">
            <v-tooltip location="bottom" open-delay="500">
              <template v-slot:activator="{ props: tooltipActivator }">
                <div
                  v-bind="onMenu"
                  class="action-button justify-center"
                  data-test="change-color-button"
                >
                  <!-- eslint-disable vue/valid-v-on vue/no-parsing-error -->
                  <v-avatar
                    size="14"
                    class="m-auto color-item"
                    :color="color"
                    v-bind="tooltipActivator"
                  ></v-avatar>
                  <!-- eslint-enable vue/valid-v-on vue/no-parsing-error -->
                </div>
              </template>

              <div>
                <p class="text-body-2 mb-1">
                  Select color
                </p>
              </div>
            </v-tooltip>
          </template>
        </workflow-settings-card-type-color-picker>

        <!-- delete connection -->
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              tabindex="0"
              data-test="delete-connection-button"
              class="action-button captures-description"
              :class="{ 'disabled': isLeafNode || disableSingleSelectionActions }"
              @click="initiateDeleteConnection"
            >
              <v-icon size="small">mdi-link-off</v-icon>
            </div>
          </template>
          <div>
            <p v-if="disableSingleSelectionActions" class="text-body-2 mb-1">
              Delete connection (disabled for multi-selection)
            </p>
            <p v-else-if="isLeafNode">
              Delete connection (disabled, no connection can be deleted)
            </p>
            <p v-else-if="hasRemovableConnection > 0" class="text-body-2 mb-1">
              Delete connection
            </p>
            <p v-else class="text-body-2 mb-1">
              Delete connection (can create a new starting point)
            </p>
          </div>
        </v-tooltip>

        <!-- delete event -->
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="{ ...props }"
              class="action-button"
              :class="{'disabled': !canDeleteEvents}"
              data-test="delete-event-button"
              @click="handleDeleteClick"
            >
              <v-icon size="small">mds:delete</v-icon>
            </div>
          </template>

          <div v-if="canDeleteEvents">
            <p class="text-body-2 mb-1">
              {{ isMultiSelection ? `Delete ${selectedEventIds.length} Events` : 'Delete Event' }}
            </p>
            <p class="mb-0" v-if="!isMultiSelection">
              <code>Alt / ⌥ + Backspace</code>
            </p>
          </div>
          <div v-else>
            <p class="text-body-2 mb-1">Delete (disabled)</p>
            <p class="mb-0">
              <span v-if="isMultiSelection">
                You cannot delete these events because the selection has more than one connection in or out, so the workflow can't be reconnected unambiguously.
              </span>
              <span v-else>
                Select an event to delete.
              </span>
            </p>
          </div>
        </v-tooltip>
      </div>
      <workflow-delete-item-dialog
        ref="deleteItemDialog"
        :itemType="'Event'"
        :hideIcon="true"
        :selectedEventIds="selectedEventIds"
        :isMultiSelection="isMultiSelection"
        @delete-multiple-events="deleteMultipleEvents"
      ></workflow-delete-item-dialog>

      <!-- add event -->
      <div
        class="action-button-group"
        :data-test-actions-container="selectedEvent.name"
      >
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              tabindex="0"
              data-test="add-event-button"
              class="action-button captures-description"
              :class="{ disabled: disableSingleSelectionActions }"
              @click="!disableSingleSelectionActions && $emit('add-shape', { type: 'bpmn:Task' })"
            >
              <v-icon size="small">custom:TaskIcon</v-icon>
            </div>
          </template>

          <template #default>
            <div>
              <p class="text-body-2 mb-1">
                Add event
                <span v-if="disableSingleSelectionActions">(disabled for multi-selection)</span>
              </p>
              <p v-if="!disableSingleSelectionActions" class="mb-0">
                <code>Alt / ⌥ + Arrow Right / Up / Down</code>
              </p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- add decision -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              tabindex="0"
              data-test="add-decision-button"
              class="action-button captures-description"
              :class="{ disabled: disableSingleSelectionActions }"
              @click="!disableSingleSelectionActions && ($emit('add-shape', { type: 'bpmn:ExclusiveGateway' }), $event.currentTarget.blur())"
            >
              <v-icon size="small">custom:DecisionIcon</v-icon>
            </div>
          </template>

          <template #default>
            <div>
              <p class="text-body-2 mb-1">
                Add decision
                <span v-if="disableSingleSelectionActions">(disabled for multi-selection)</span>
              </p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- add connection -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              class="action-button"
              :class="[ enableConnectionTargets ? 'active' : '', { disabled: addConnectionDisabled || disableSingleSelectionActions } ]"
              data-test="add-connection-button"
              @click="!disableSingleSelectionActions && eventBusEmitEnableTargets(); $event.currentTarget.blur();"
            >
              <v-icon size="small">mdi-link-plus</v-icon>
            </div>
          </template>

          <template #default>
            <div>
              <p v-if="disableSingleSelectionActions" class="text-body-2 mb-1">
                Add connection (disabled for multi-selection)
              </p>
              <p v-else class="text-body-2 mb-1">Add connection</p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- add parallel event -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              tabindex="0"
              data-test="add-parallel-event-button"
              class="action-button captures-description"
              :class="{ disabled: addParallelEventDisabled }"
              @click="!addParallelEventDisabled && $emit('add-parallel-event')"
            >
              <v-icon size="small">mdi-call-split</v-icon>
            </div>
          </template>

          <template #default>
            <div>
              <p v-if="!addParallelEventDisabled" class="text-body-2 mb-1">
                Add parallel event
              </p>
              <p v-else class="text-body-2 mb-1">
                Add parallel event (disabled, {{ addParallelEventDisabledReason }})
              </p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- add subprocess -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props: tooltipActivator }">
            <bpmn-diagram-actions-shape-top-transform
              :selectedEvent="selectedEvent"
              :base-data="baseData"
              :svgGraph="svgGraph"
              :svgGroups="svgGroups"
              :svgLanes="svgLanes"
              @add-shape="$emit('add-shape', $event)"
              class="text-center"
            >
              <template v-slot:buttons="props">
                <div
                  v-bind="tooltipActivator"
                  data-test="add-subprocess-button"
                  class="action-button"
                  :class="{ disabled: disableSingleSelectionActions }"
                  @click="!disableSingleSelectionActions && props.openDialog()"
                >
                  <v-icon style="width: 22px; height: 20px" size="small">custom:SubworkflowIcon</v-icon>
                </div>
              </template>
            </bpmn-diagram-actions-shape-top-transform>
          </template>

          <template #default>
            <div>
              <p class="text-body-2 mb-1">
                Add subprocess
                <span v-if="disableSingleSelectionActions">(disabled for multi-selection)</span>
              </p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- Cut, copy, paste -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template #activator="{ props }">
            <v-menu
              density="compact"
              scrim="true"
              @update:model-value="onClipboardActionsMenuToggle"
            >
              <template #activator="{ props: clipboardMenuProps }">
                <div
                  v-bind="clipboardMenuProps"
                  class="action-button"
                >
                  <v-icon v-bind="props">mdi-content-cut</v-icon>
                </div>
              </template>
              <v-list>
                <v-tooltip location="right">
                  <template #activator="{ props: itemProps }">
                    <div v-bind="itemProps">
                      <v-list-item
                        :disabled="!canCutEvents"
                        prepend-icon="mdi-content-cut"
                        @click="canCutEvents && $emit('on-cut')"
                      >
                        <v-list-item-title>Cut</v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  <span v-if="canCutEvents">
                    <code>Ctrl / Cmd + X</code>
                  </span>
                  <span v-else>Cut (disabled). You cannot cut these events because it would break the workflow connections.</span>
                </v-tooltip>
                <v-tooltip location="right">
                  <template #activator="{ props: itemProps }">
                    <div v-bind="itemProps">
                      <v-list-item
                        :disabled="!canCopyEvents"
                        prepend-icon="mdi-content-copy"
                        @click="canCopyEvents && $emit('on-copy')"
                      >
                        <v-list-item-title>Copy</v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  <span v-if="canCopyEvents">
                    <code>Ctrl / Cmd + C</code>
                  </span>
                  <span v-else>Copy (disabled). You cannot copy these events because they form an invalid selection.</span>
                </v-tooltip>
                <v-tooltip location="right">
                  <template #activator="{ props: itemProps }">
                    <div v-bind="itemProps">
                      <v-list-item
                        :disabled="!clipboardHasValidJson"
                        prepend-icon="mdi-content-paste"
                        @click="clipboardHasValidJson && $emit('on-paste')"
                      >
                        <v-list-item-title>Paste</v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  <span v-if="clipboardHasValidJson">
                    <code>Ctrl / Cmd + V</code>
                  </span>
                  <span v-else>No valid event data in clipboard</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </template>

          <template #default>
            <div>
              <p class="text-body-2 mb-1">
                Clipboard actions
                <span v-if="!canCutEvents && !canCopyEvents"> (some actions disabled)</span>
              </p>
              <p v-if="!canCutEvents || !canCopyEvents" class="mb-0 text-caption">
                Cut/Copy disabled for invalid selection
              </p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- add event with AI -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props: tooltipActivator }">
            <div
              v-bind="tooltipActivator"
              class="action-button"
              :class="{'disabled': disableSingleSelectionActions}"
              @click="!disableSingleSelectionActions && $emit('add-shape-ai', selectedEvent.id); $event.currentTarget.blur();"
            >
              <v-icon style="width: 22px; height: 20px" size="small">mds:temp-preferences-custom</v-icon>
            </div>
          </template>

          <template #default>
            <div>
              <p v-if="disableSingleSelectionActions" class="text-body-2 mb-1">
                Add event with AI (disabled, multiple events selected)
              </p>
              <p v-else class="text-body-2 mb-1">Add event with AI</p>
            </div>
          </template>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import BpmnDiagramActionsShapeTopTransform from '@/components/BpmnDiagramActionsShapeTopTransform'
import WorkflowSettingsCardTypeColorPicker from '@/components/WorkflowSettingsCardTypeColorPicker'
import { workflowApiHelpers } from '@/api/workflowApiHelpers'
import { EventBus } from '@/event-bus/event-bus.js'
import workflowApi from '@/api/workflowApi'
import BpmnDeleteConnectionDialog from '@/components/BpmnDeleteConnectionDialog'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import WorkflowDeleteItemDialog from '@/components/WorkflowDeleteItemDialog'
import copyPasteHelpers from '@/helpers/copyPasteHelpers'
import { mapGetters } from 'vuex'

export default {
  components: {
    BpmnDiagramActionsShapeTopTransform,
    WorkflowSettingsCardTypeColorPicker,
    BpmnDeleteConnectionDialog,
    WorkflowDeleteItemDialog
  },
  mixins: [diagramHelpers],
  props: {
    selectedEvent: Object,
    selectedEventIds: {
      type: Array,
      default: () => []
    },
    enableConnectionTargets: Boolean,
    active: Boolean,
    color: String,
    events: Array,
    baseData: Object,
    svgGraph: Object,
    svgGroups: Object,
    svgLanes: Object,
    workflow: Object,
    disableSingleSelectionActions: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      connections: [],
      confirmDialog: false,
      clipboardHasValidJson: false,
      clipboardActionsMenuOpen: false
    }
  },

  methods: {
    async checkClipboardValidity () {
      this.clipboardHasValidJson = await copyPasteHelpers.checkClipboardValidity()
    },
    async onClipboardActionsMenuToggle (isOpen) {
      this.clipboardActionsMenuOpen = isOpen
      if (isOpen) {
        await this.checkClipboardValidity()
      }
    },
    changeColor (color) {
      const colorObj = { ...color, rgb: `rgb(${color.red},${color.green},${color.blue})` }

      // If multiple events are selected, update all of them
      if (this.disableSingleSelectionActions && Array.isArray(this.selectedEventIds) && this.selectedEventIds.length > 1) {
        const updatedNames = []
        this.selectedEventIds.forEach(eventId => {
          const event = this.svgGraph[eventId]
          if (event) {
            event.color = colorObj
            updatedNames.push(event.name)
          }
        })
        if (updatedNames.length > 0) {
          workflowApi.updateWorkflow({
            svgGraph: this.svgGraph,
            svgLanes: this.svgLanes,
            workflow: this.workflow,
            updateNote: {
              action: 'changed color of',
              target: updatedNames.length === 1 ? updatedNames[0] : `${updatedNames.length} events`
            }
          }, true)
          // Clear multi-selection after color change
          this.$emit('clear-multi-selection')
        }
      } else {
        // Single event selection
        const eventId = this.$route.params.eventId
        if (!Object.hasOwn(this.svgGraph, eventId)) {
          return
        }

        const selectedEvent = this.svgGraph[eventId]
        selectedEvent.color = { ...color, rgb: `rgb(${color.red},${color.green},${color.blue})` }

        workflowApi.updateWorkflow({
          svgGraph: this.svgGraph,
          svgLanes: this.svgLanes,
          workflow: this.workflow,
          updateNote: {
            action: 'changed color of',
            target: selectedEvent.name
          }
        }, true)
      }
    },
    initiateDeleteConnection () {
      if (this.isLeafNode || !this.selectedEventId) return
      const parentId = this.selectedEventId
      this.connections = []
      const childIds = this.svgGraph[parentId]?.childIds || []
      if (childIds.length > 1) {
        this.showConfirmationDialog(parentId, childIds)
      } else if (childIds.length === 1) {
        this.deleteConnection(parentId, childIds[0])
      }
    },
    showConfirmationDialog (parentId, childIds) {
      childIds.forEach(childId => {
        const target = this.svgGraph[childId]
        this.connections.push({ parentId, childId, eventName: target.name })
      })
      this.confirmDialog = true
    },
    eventBusEmitEnableTargets () {
      if (this.addConnectionDisabled) {
        return
      }
      EventBus.$emit('enable-connection-targets', !this.enableConnectionTargets)
    },
    deleteConnection (parentId, childId) {
      this.confirmDialog = false
      if (!parentId || !childId) {
        return
      }
      workflowApi.deleteConnectionFromEvent(parentId, childId, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
    },
    moveDownInEventGroup () {
      if (!this.selectedEventId) {
        return
      }
      this.svgGraph[this.selectedEventId].offset++
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: JSON.parse(JSON.stringify(this.svgLanes)),
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'moved down event',
          target: this.svgGraph[this.selectedEventId].name
        }
      })
    },
    handleDeleteClick () {
      // Check if events can be deleted
      if (!this.canDeleteEvents) {
        return
      }

      // For multi-selection, open dialog (validation already passed)
      if (this.isMultiSelection) {
        if (this.$refs.deleteItemDialog) {
          this.$refs.deleteItemDialog.openDialog()
        }
        return
      }

      // For single selection, open dialog
      if (this.$refs.deleteItemDialog) {
        this.$refs.deleteItemDialog.openDialog()
      }
    },
    async deleteMultipleEvents (eventIds) {
      const idsToDelete = eventIds || this.selectedEventIds
      if (!Array.isArray(idsToDelete) || idsToDelete.length === 0) {
        return
      }

      // Clear selection
      this.$emit('clear-multi-selection')
      await this.$nextTick()

      // Delete events and update workflow
      await workflowApi.deleteEventsFromWorkflow(idsToDelete, this.svgGraphData, this.svgLanesData, this.svgGroupsData, this.workflow)

      this.$router.push({
        path: '/workflow/' + this.$route.params.projectId + '/' + this.workflow.id
      }).catch(() => {})
    }
  },

  computed: {
    ...mapGetters({
      svgGraphData: 'svgGraphData/svgGraphData',
      svgLanesData: 'svgGraphData/svgLanesData',
      svgGroupsData: 'svgGraphData/svgGroupsData'
    }),
    selectedEventId () {
      return this.selectedEvent?.id || null
    },
    isMultiSelection () {
      return this.disableSingleSelectionActions && Array.isArray(this.selectedEventIds) && this.selectedEventIds.length > 1
    },
    isLeafNode () {
      if (!this.selectedEventId) {
        return true
      }
      return !this.svgGraph[this.selectedEventId]?.childIds?.length
    },
    addParallelEventDisabled () {
      if (this.disableSingleSelectionActions) return true
      if (!this.selectedEvent) return true
      // Need at least one child to create a parallel branch
      if (!this.selectedEvent.childIds?.length) return true
      // Decisions already handle multiple children natively
      if (this.selectedEvent.type === 'bpmn:ExclusiveGateway') return true
      return false
    },
    addParallelEventDisabledReason () {
      if (this.disableSingleSelectionActions) return 'multiple events selected'
      if (!this.selectedEvent) return 'no event selected'
      if (!this.selectedEvent.childIds?.length) return 'add a child event first'
      if (this.selectedEvent.type === 'bpmn:ExclusiveGateway') return 'decisions already support multiple children'
      return ''
    },
    addConnectionDisabled () {
      return !this.selectedEvent
    },
    hasRemovableConnection () {
      if (!this.selectedEventId) {
        return 0
      }
      return workflowApiHelpers.getRemovableSequenceFlows(this.events, this.selectedEventId).length
    },
    atTheBottomOfLane () {
      if (!this.selectedEventId) {
        return false
      }
      return this.currentEventIsAtTheBottomOfItsLane(this.svgLanes, this.svgGraph, this.selectedEventId)
    },
    transformMenuDisabled () {
      return this.disableSingleSelectionActions
    },
    transformDisabledReason () {
      if (this.disableSingleSelectionActions) {
        return 'multiple selection'
      }

      return ''
    },
    // Cut/copy gate: require a connected selection (pasteable fragment).
    canModifyMultiSelection () {
      return this.canMultiSelectionBeModified(this.selectedEventIds, this.svgGraph)
    },
    // Delete gate: re-stitch is per-node, so disconnected is fine; only need a
    // clean boundary (≤1 external parent, ≤1 external child).
    canDeleteMultiSelection () {
      return this.canSafelyDeleteMultiSelectedEvents(this.selectedEventIds, [], this.svgGraph)
    },
    canDeleteEvents () {
      if (this.isMultiSelection) {
        return this.canDeleteMultiSelection
      }
      // Single selection: any selected event can be deleted (its children
      // are re-parented to the parent, which now supports parallel children)
      return !!this.selectedEventId
    },
    canCutEvents () {
      if (this.isMultiSelection) {
        return this.canModifyMultiSelection
      }
      // Single selection: any selected event can be cut
      return !!this.selectedEventId
    },
    canCopyEvents () {
      if (this.isMultiSelection) {
        return this.canModifyMultiSelection
      }
      // For single selection, always allow copy
      return true
    }
  },

  watch: {
    confirmDialog: function (val) {
      if (!val) {
        this.connections = []
      }
    }
  }
}
</script>

<style scoped>
.action-menu {
  display: flex;
  justify-content: space-around;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  background-color: var(--light-1);
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.15);
}

.action-button-group {
  display: flex;
  justify-content: space-around;
  gap: 8px;
}

.action-button-group:not(:first-child) {
  padding-left: 8px;
  border-left: 1px solid #E5E2E1;
}

[data-theme="dark"] .action-menu {
  border-color: var(--light-4);
}

[data-theme="dark"] .action-button-group:not(:first-child) {
  border-left-color: var(--light-4);
}

.action-button-group > span {
  display: none;
}

.action-button {
  padding: 0;
  margin: -4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  background-color: var(--light-1);
  transition: background-color ease 0.2s;
  cursor: pointer;
}

.action-button:hover {
  background-color: var(--light-2);
}

.action-button:active {
  background-color: var(--light-3);
}

.action-button.active {
  background-color: var(--light-3);
}

.action-button:hover.active {
  background-color: var(--light-4);
}

.action-button.disabled {
  background-color: var(--light-1);
}

.action-button > .v-icon {
  font-size: 20px;
  margin: auto;
  color: var(--dark-4) !important;
}

.action-button.disabled > .v-icon {
  color: #dadee4 !important;
}

[data-theme="dark"] .action-button.disabled > .v-icon {
  color: var(--dark-1) !important;
}

.captures-description:focus-visible {
  outline: none;
}
</style>
